import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Grid, Chip } from '@material-ui/core';
import { CheckCircle as CheckIcon } from '@material-ui/icons';
import { ADMIN_RUNTIME_COMMANDS } from '../../../../app/runtime/adminRuntime';
import styles from './ExperienceSelectionStep.module.scss';
import WizardStepLayout from '../../components/common/WizardStepLayout';
import { normalizeExperienceType } from '../../experienceTypes';
import { SEARCH_EXPERIENCES as EXPERIENCES } from '../../experienceOptions';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

const ExperienceSelectionStep = ({ data, onUpdate, onReady, runtime }) => {
  const localizeMessage = useLocalizedMessage();
  const savedExperience = data?.selectedExperience;
  const defaultPending = useRef(savedExperience === undefined);
  const inputRefs = useRef({});
  const [selected, setSelected] = useState(
    () => (savedExperience === undefined ? 'unified' : normalizeExperienceType(savedExperience)),
  );

  const handleSelect = useCallback((type) => {
    setSelected(type);
    onUpdate({ selectedExperience: type });

    if (!runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.ONBOARDING_MODE_SELECTED)) {
      return;
    }

    // Product runtime owns plugin checkpoints; the cloud adapter is a no-op.
    // This remains fire-and-forget so a lifecycle signal cannot discard the
    // merchant's local selection.
    runtime.sendMessageWithResponse(
      ADMIN_RUNTIME_COMMANDS.ONBOARDING_MODE_SELECTED,
      { selectedMode: type },
    ).catch(() => {});
  }, [onUpdate, runtime]);

  useEffect(() => {
    if (savedExperience === undefined && defaultPending.current) {
      defaultPending.current = false;
      handleSelect('unified');
    } else if (savedExperience !== undefined) {
      defaultPending.current = false;
      setSelected(normalizeExperienceType(savedExperience));
    }
  }, [savedExperience, handleSelect]);

  useEffect(() => {
    if (onReady) {
      onReady(!!selected);
    }
  }, [selected, onReady]);

  const handleKeyDown = (event, index) => {
    const lastIndex = EXPERIENCES.length - 1;
    const nextIndex = {
      ArrowRight: (index + 1) % EXPERIENCES.length,
      ArrowDown: (index + 1) % EXPERIENCES.length,
      ArrowLeft: (index + lastIndex) % EXPERIENCES.length,
      ArrowUp: (index + lastIndex) % EXPERIENCES.length,
      Home: 0,
      End: lastIndex,
    }[event.key];

    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextType = EXPERIENCES[nextIndex].type;
    inputRefs.current[nextType].focus();
    handleSelect(nextType);
  };

  return (
    <WizardStepLayout
      title={localizeMessage('wizard.searchSetup.experience.title')}
      description={localizeMessage('wizard.searchSetup.experience.description')}
      maxWidth="wide"
    >
      <Grid
        container
        spacing={3}
        role="radiogroup"
        aria-label={localizeMessage('wizard.searchSetup.experience.title')}
      >
        {EXPERIENCES.map((experience, index) => (
          <Grid item xs={12} md={4} key={experience.type}>
            <Paper
              component="label"
              className={`${styles.experienceCard} ${
                selected === experience.type ? styles.selected : ''
              }`}
              elevation={0}
            >
              <input
                ref={(element) => { inputRefs.current[experience.type] = element; }}
                className={styles.radioInput}
                type="radio"
                name="search-experience"
                value={experience.type}
                checked={selected === experience.type}
                tabIndex={selected === experience.type || (!selected && index === 0) ? 0 : -1}
                aria-labelledby={`experience-${experience.type}-title`}
                aria-describedby={`experience-${experience.type}-description`}
                onChange={() => handleSelect(experience.type)}
                onKeyDown={(event) => handleKeyDown(event, index)}
              />
              <Box className={styles.imageContainer}>
                <img src={experience.image} alt="" width="680" height="380" />
              </Box>

              <Box className={styles.cardContent}>
                <Box className={styles.cardEyebrow}>
                  <span className={styles.selectionIndicator} aria-hidden="true">
                    {selected === experience.type && <CheckIcon />}
                  </span>
                  {experience.recommended && (
                    <Chip
                      label={localizeMessage('wizard.searchSetup.experience.recommended')}
                      size="small"
                      className={styles.recommendedChip}
                    />
                  )}
                </Box>

                <Typography
                  id={`experience-${experience.type}-title`}
                  variant="h5"
                  component="h2"
                  className={styles.cardTitle}
                >
                  {localizeMessage(experience.nameKey)}
                </Typography>

                <Typography
                  id={`experience-${experience.type}-description`}
                  variant="body2"
                  className={styles.cardDescription}
                >
                  {localizeMessage(experience.descriptionKey)}
                </Typography>

                <Box component="ul" className={styles.featuresList}>
                  {experience.features.map((featureKey) => (
                    <Typography
                      key={featureKey}
                      component="li"
                      variant="body2"
                      className={styles.feature}
                    >
                      {localizeMessage(featureKey)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </WizardStepLayout>
  );
};

ExperienceSelectionStep.propTypes = {
  data: PropTypes.shape({
    selectedExperience: PropTypes.string,
  }),
  onUpdate: PropTypes.func.isRequired,
  onReady: PropTypes.func,
  runtime: PropTypes.shape({
    supports: PropTypes.func.isRequired,
    sendMessageWithResponse: PropTypes.func.isRequired,
  }),
};

ExperienceSelectionStep.defaultProps = {
  data: {},
  onReady: null,
  runtime: null,
};

export default ExperienceSelectionStep;
